import { CSSProperties, useState } from 'react';
import { FormattedMessage, defineMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Play } from '@sorare/core/src/atoms/icons/Play';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BodyL, BodyS } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog/Dialog';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { VideoDuration } from './VideoDuration';
import {
  competitionsVideo,
  getStartedVideo,
  introductionVideo,
  marketVideo,
  scoringVideo,
} from './data';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;
const VideoWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  height: 100%;
  position: relative;
  isolation: isolate;

  @media ${laptopAndAbove} {
    height: auto;
    aspect-ratio: 16 / 9;
  }
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  padding: var(--double-unit);
`;
const VideoPreview = styled(LinkBox)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  padding: var(--double-unit);
  overflow: hidden;
`;
const MarketVideoPreview = styled(VideoPreview)`
  border-radius: var(--double-unit);
  width: 100%;
  min-height: calc(44 * var(--unit));
`;
const MarketVideoPreviewBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(to top right, var(--c-nl-700), var(--c-nl-900)),
    center / cover no-repeat var(--url);
  mix-blend-mode: luminosity;
`;
const SecondaryVideoPreview = styled(VideoPreview)`
  border-radius: var(--intermediate-unit);
  width: 100%;
  min-height: calc(36 * var(--unit));
`;
const SecondaryVideoPreviewBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    linear-gradient(to top right, var(--c-nl-800), var(--c-nl-800)),
    center / cover no-repeat var(--url);
`;
const GradientTitle = styled.span`
  font-family: var(--rigid-square);
  text-transform: uppercase;
  font-weight: var(--t-bold);
  background: var(--c-gradient-conversionCreditBorder);
  background-size: 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -moz-text-fill-color: transparent;
`;
const PartTitle = styled(GradientTitle)`
  font-size: var(--fs-12);
`;
const TransferMarketTitle = styled(GradientTitle)`
  font-size: var(--fs-32);
  line-height: 1;
`;
const PreviewFirstLine = styled.div`
  align-self: flex-start;
`;
const StyledLinkOverlay = styled(LinkOverlay)`
  &:hover,
  &:focus {
    opacity: 0.9;
  }
`;
const SecondaryVideoPreviewPlayButtonWrapper = styled.div`
  align-self: center;
`;

const secondaryVideos = [
  introductionVideo,
  getStartedVideo,
  scoringVideo,
  competitionsVideo,
];

const partMessage = defineMessage({
  id: 'MarketVideoDialog.videoPreview.chapter',
  defaultMessage: 'Part {integer}',
});

type Props = { open: boolean; onClose: () => void };
export const MarketVideoDialog = ({ open, onClose }: Props) => {
  const { locale } = useIntlContext();
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const marketSources =
    marketVideo[locale as keyof Omit<typeof marketVideo, 'key'>] ||
    marketVideo.default;

  const onCloseVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <Root>
        <MarketVideoPreview>
          <MarketVideoPreviewBackground
            style={
              {
                '--url': `url(${marketSources.thumbnail})`,
              } as CSSProperties
            }
          />
          <PreviewFirstLine>
            <VideoDuration durationInMinutes={marketSources.duration} />
          </PreviewFirstLine>
          <Vertical gap={2} center className="text-center">
            <PartTitle>
              <FormattedMessage {...partMessage} values={{ integer: 1 }} />
            </PartTitle>
            <TransferMarketTitle>
              <FormattedMessage
                id="MarketVideo.transferMarket"
                defaultMessage="Transfer Market"
              />
            </TransferMarketTitle>
          </Vertical>
          <SBHorizontal gap={4}>
            <BodyS>
              <FormattedMessage
                id="MarketVideo.startYourJourney"
                defaultMessage="Kick off your journey with this beginner-friendly guide!"
              />
            </BodyS>
            <StyledLinkOverlay
              as={ButtonBase}
              onClick={() => {
                setSelectedVideo(marketSources.src);
              }}
            >
              <IconButton asSpan color="secondary">
                <Play />
              </IconButton>
            </StyledLinkOverlay>
          </SBHorizontal>
        </MarketVideoPreview>
        <Vertical gap={2}>
          <Vertical gap={0}>
            <BodyL bold>
              <FormattedMessage {...glossary.continue} />
            </BodyL>
            <BodyL color="var(--c-nd-700)">
              <FormattedMessage
                id="MarketVideoDialog.subtitle"
                defaultMessage="Master the basics"
              />
            </BodyL>
          </Vertical>
          <Scrollable itemToDisplay={2} overhang={4}>
            {secondaryVideos.map((videoData, index) => {
              const sources =
                videoData[
                  locale as keyof Omit<typeof videoData, 'key' | 'title'>
                ] || videoData.default;
              return (
                <SecondaryVideoPreview key={videoData.key}>
                  <SecondaryVideoPreviewBackground
                    style={
                      {
                        '--url': `url(${sources.thumbnail})`,
                      } as CSSProperties
                    }
                  />
                  <PreviewFirstLine>
                    <VideoDuration durationInMinutes={sources.duration} />
                  </PreviewFirstLine>
                  <SecondaryVideoPreviewPlayButtonWrapper>
                    <StyledLinkOverlay
                      as={ButtonBase}
                      onClick={() => {
                        setSelectedVideo(sources.src);
                      }}
                    >
                      <IconButton asSpan color="secondary">
                        <Play />
                      </IconButton>
                    </StyledLinkOverlay>
                  </SecondaryVideoPreviewPlayButtonWrapper>
                  <Vertical gap={0}>
                    <PartTitle>
                      <FormattedMessage
                        {...partMessage}
                        values={{ integer: index + 2 }}
                      />
                    </PartTitle>
                    <BodyS>
                      <FormattedMessage {...videoData.title} />
                    </BodyS>
                  </Vertical>
                </SecondaryVideoPreview>
              );
            })}
          </Scrollable>
        </Vertical>
      </Root>
      {selectedVideo && (
        <Dialog
          onClose={onCloseVideo}
          open={!!selectedVideo}
          maxWidth="md"
          fullWidth
          hideHeader
        >
          {({ CloseButton }) => (
            <VideoWrapper>
              <CloseButtonWrapper>
                <CloseButton onClose={onCloseVideo} />
              </CloseButtonWrapper>
              <iframe
                width="100%"
                height="100%"
                src={selectedVideo}
                title="YouTube video player"
                style={{ border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </VideoWrapper>
          )}
        </Dialog>
      )}
    </Dialog>
  );
};
